/**
 * Mission Settings Types
 *
 * Type definitions for mission configuration and validation
 */

import type { Mission, VehicleType } from './mission.types';

/**
 * Firmware types supported by the application
 */
export enum FirmwareType {
  ARDUPILOT = 'ArduPilot',
  PX4 = 'PX4',
}

/**
 * Mission settings interface
 * Contains default values and configuration for mission planning
 */
export interface MissionSettings {
  // Flight parameters
  defaultAltitude: number;      // Default waypoint altitude (meters)
  defaultSpeed: number;         // Default flight speed (m/s)
  cruiseSpeed: number;          // Cruise speed for long segments (m/s)
  maxSpeed: number;             // Maximum allowed speed (m/s)

  // Vehicle configuration
  vehicleType: VehicleType;     // Type of vehicle (Copter/Plane/VTOL/etc)
  firmwareType: FirmwareType;   // Firmware type (ArduPilot/PX4)

  // Safety settings
  safetySettings?: SafetySettings;
}

/**
 * Safety configuration settings
 */
export interface SafetySettings {
  maxAltitude: number;          // Maximum altitude limit (meters)
  minAltitude: number;          // Minimum altitude limit (meters)
  maxDistance: number;          // Maximum distance from home (meters)
  batteryReserve: number;       // Minimum battery reserve percentage
  geofenceEnabled: boolean;     // Enable geofence checking
  rtlAltitude?: number;         // Return-to-launch altitude (meters)
}

/**
 * Validation result levels
 */
export enum ValidationLevel {
  ERROR = 'error',      // Critical issue - blocks mission upload
  WARNING = 'warning',  // Non-critical issue - can proceed with caution
  INFO = 'info',        // Informational - suggestions for improvement
}

/**
 * Validation result for mission or waypoint
 */
export interface ValidationResult {
  id: string;                   // Unique validation ID
  level: ValidationLevel;       // Severity level
  message: string;              // Human-readable message
  affectedItems: string[];      // IDs of affected waypoints/items
  suggestedFix?: string;        // Optional suggestion for fixing
  timestamp: Date;              // When validation was performed
}

/**
 * Complete validation state for a mission
 */
export interface MissionValidation {
  isValid: boolean;             // Overall validation status (no errors)
  canUpload: boolean;           // Whether mission can be uploaded (no blocking errors)
  results: ValidationResult[];  // All validation results
  lastValidated: Date;          // Timestamp of last validation
}

/**
 * Validation category for grouping results
 */
export enum ValidationCategory {
  WAYPOINTS = 'waypoints',
  ALTITUDE = 'altitude',
  DISTANCE = 'distance',
  BATTERY = 'battery',
  SAFETY = 'safety',
  COMMANDS = 'commands',
  GENERAL = 'general',
}

/**
 * Validation rule definition
 */
export interface ValidationRule {
  id: string;
  category: ValidationCategory;
  level: ValidationLevel;
  check: (mission: Mission) => ValidationResult | null;
  enabled: boolean;
}
